import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { uploadPhotos } from '../utils/uploadPhotos';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import PhotoGallery from './PhotoGallery';

const DEFAULT_PHOTO_TYPES = ['habitat', 'cap_top', 'fertile_layer', 'stipe', 'cross_section', 'microscopy', 'other'];

const DELETE_ROLES = ['collection_manager', 'internal_expert'];
const LAB_DELETE_ROLES = ['collection_manager', 'lab_manager']; // internal_expert is view-only on lab data

/**
 * Props:
 *   fetchUrl    — e.g. `/collections/${id}/photos`
 *   uploadBody  — extra fields for POST /photos, e.g. { sample_id: '...' }
 *   taxonName   — used for S3 folder organisation
 *   sourceType  — 'field_collection' | 'lab' | 'external_collection' (matches the photo_source ENUM)
 *   photoTypes  — optional override list of selectable photo_type values
 *
 * Several photographs can be chosen at once from the gallery (or taken one
 * after another with the camera); each is queued with its own type and note
 * and they upload together. A photo that fails stays in the queue with the
 * others removed, so a retry sends only what did not arrive.
 */
export default function PhotoSection({ annotatable = false, fetchUrl, uploadBody, taxonName, sourceType, photoTypes = DEFAULT_PHOTO_TYPES }) {
  const { user } = useAuth();
  const { t: tc } = useTranslation('common');
  const canDelete = sourceType === 'lab'
    ? user?.roles?.some(r => LAB_DELETE_ROLES.includes(r))
    : user?.roles?.some(r => DELETE_ROLES.includes(r));

  const [photos, setPhotos]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [queue, setQueue]             = useState([]);   // [{ id, file, photoType, notes, previewUrl }]
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [deleteError, setDeleteError]   = useState('');

  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);
  const nextId     = useRef(0);

  const fetchPhotos = () => {
    setLoading(true);
    api.get(fetchUrl)
      .then(res => setPhotos(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPhotos(); }, [fetchUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Object URLs hold the file in memory until revoked.
  useEffect(() => () => queue.forEach(q => q.previewUrl && URL.revokeObjectURL(q.previewUrl)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = (files) => {
    const added = Array.from(files || []).map(file => ({
      id: nextId.current++,
      file,
      photoType: photoTypes[0],
      notes: '',
      previewUrl: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '',
    }));
    if (added.length) {
      setQueue(q => [...q, ...added]);
      setUploadError('');
    }
  };

  const updateItem = (id, patch) => setQueue(q => q.map(item => (item.id === id ? { ...item, ...patch } : item)));
  const removeItem = (id) => setQueue(q => {
    const gone = q.find(item => item.id === id);
    if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
    return q.filter(item => item.id !== id);
  });

  const resetForm = () => {
    queue.forEach(q => q.previewUrl && URL.revokeObjectURL(q.previewUrl));
    setQueue([]);
    setUploadError('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (queue.length === 0) { setUploadError('Please select at least one photo.'); return; }
    const undescribed = queue.filter(q => q.photoType === 'other' && !q.notes.trim());
    if (undescribed.length > 0) {
      setUploadError(`A description is required for "other" photos (${undescribed.map(q => q.file.name).join(', ')}).`);
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const result = await uploadPhotos(
        queue.map(q => ({ file: q.file, photoType: q.photoType, notes: q.notes.trim() })),
        uploadBody, taxonName, sourceType
      );
      fetchPhotos();
      if (result.failed.length > 0) {
        // Keep exactly the ones that did not arrive.
        const failedNames = new Set(result.failed.map(f => f.filename));
        setQueue(q => q.filter(item => failedNames.has(item.file.name)));
        setUploadError(`${result.failed.length} of ${queue.length} photo(s) failed to upload: ` +
          `${result.failed.map(f => f.filename).join(', ')}. Try again.`);
        return;
      }
      resetForm();
      setShowForm(false);
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const confirmDeletePhoto = () => {
    setDeletingPhoto(true);
    setDeleteError('');
    api.delete(`/photos/${deleteTarget}`)
      .then(() => { setDeleteTarget(null); fetchPhotos(); })
      .catch(err => setDeleteError(err.response?.data?.error || 'Delete failed'))
      .finally(() => setDeletingPhoto(false));
  };

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Photos{photos.length > 0 ? ` (${photos.length})` : ''}</strong>
        <button
          onClick={() => { setShowForm(s => !s); resetForm(); }}
          title={showForm ? tc('tip.photoUploadClose') : tc('tip.photoUploadOpen')}
          style={btnStyle('var(--c-primary)')}
        >
          {showForm ? 'Cancel' : '+ Upload'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleUpload} style={subFormStyle} data-testid="photo-upload-form">
          {uploadError && <p style={errStyle}>{uploadError}</p>}
          <div style={{ display: 'flex', gap: 8, marginBottom: queue.length ? 12 : 0, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => cameraRef.current?.click()} title={tc('tip.photoCamera')} style={btnStyle('var(--c-info)')}>
              📷 Camera
            </button>
            <button type="button" onClick={() => galleryRef.current?.click()} title={tc('tip.photoGallery')} style={btnStyle('var(--c-text-muted)')}>
              🗂 Gallery (select several)
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" data-testid="photo-camera-input"
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
            <input ref={galleryRef} type="file" accept="image/*" multiple data-testid="photo-gallery-input"
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
          </div>

          {queue.length > 0 && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {queue.map(item => (
                  <div key={item.id} data-testid="photo-queue-item"
                       style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 6, padding: 6 }}>
                    {item.previewUrl
                      ? <img src={item.previewUrl} alt="" style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 4 }} />
                      : <span style={{ width: 56 }} />}
                    <span style={{ fontSize: 12, color: 'var(--c-text-body-fg)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.file.name}
                    </span>
                    <select value={item.photoType} aria-label={`Photo type for ${item.file.name}`}
                            onChange={e => updateItem(item.id, { photoType: e.target.value })} title={tc('tip.photoType')} style={inputStyle}>
                      {photoTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                    </select>
                    <input value={item.notes} placeholder={item.photoType === 'other' ? 'Description *' : 'Notes'}
                           aria-label={`Notes for ${item.file.name}`} title={tc('tip.photoNotes')}
                           onChange={e => updateItem(item.id, { notes: e.target.value })} style={{ ...inputStyle, width: 160 }} />
                    <button type="button" onClick={() => removeItem(item.id)} aria-label={`Remove ${item.file.name}`} title={tc('tip.photoRemove')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 18 }}>×</button>
                  </div>
                ))}
              </div>
              <button type="submit" disabled={uploading} title={tc('tip.photoUpload')} style={{ ...btnStyle('var(--c-primary)'), marginTop: 10 }}>
                {uploading ? 'Uploading…' : `Upload ${queue.length} photo${queue.length === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </form>
      )}

      {loading ? (
        <p style={dimStyle}>Loading…</p>
      ) : photos.length === 0 ? (
        <p style={dimStyle}>No photos yet.</p>
      ) : (
        <div style={{ marginTop: 8 }}>
          <PhotoGallery
            photos={photos}
            annotatable={annotatable}
            onDelete={canDelete ? (p => { setDeleteError(''); setDeleteTarget(p.photo_id); }) : undefined}
            deleteTitle="Delete photo"
          />
        </div>
      )}

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title="Delete this photo?"
        consequences={['This photo will be permanently deleted from storage. This cannot be undone.']}
        deleting={deletingPhoto}
        error={deleteError}
        onConfirm={confirmDeletePhoto}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

const dimStyle    = { fontSize: 13, color: 'var(--c-text-faint-fg)' };
const errStyle    = { color: 'red', fontSize: 13, margin: '0 0 10px' };
const subFormStyle = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 14, marginBottom: 12 };
const inputStyle  = { padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 13, fontWeight: 400 };
const btnStyle    = (bg) => ({ background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 13 });
